import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Account Transfer of Assets → Account Transfer (ACAT) / Transfer of Assets (TOA).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Automated Customer Account Transfer
 * (ACAT) Screen" + "Attach Automated Customer Account Transfer (ACAT) Change
 * Files", and "Transfer of Assets (TOA) Screen" + "Attach Transfer of Assets
 * (TOA) Files". The two sub-categories ask different questions but share the
 * same shape (details, instructions, accounts table, upload, acknowledgement),
 * so they share one guide with a question list per screen. Shared chrome and
 * policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Account Transfer of Assets',
    subCategory: 'Account Transfer (ACAT) or Transfer of Assets (TOA)',
    subject: subjectRow('ACAT transfer - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>ACAT and TOA ask different questions.</strong> Both are listed below under their own screen
        heading - prepare from the one that matches the Sub-Category you chose.
      </>
    ),
  }),
  {
    title: 'Answer the transfer details',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          <strong>ACAT:</strong> the <strong>Automated Customer Account Transfer (ACAT) Details</strong> screen reveals
          its questions one at a time, starting with the receiving custodian and the estimated value. The rest only
          appear once the estimated value is more than $1.
        </p>
        <p style={{ margin: 0 }}>
          <strong>TOA:</strong> the <strong>Transfer of Assets (TOA) Details</strong> screen shows every question at
          once. All of them are required.
        </p>
      </>
    ),
  },
  {
    title: 'Write the instructions and choose the accounts',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Additional Instructions</strong> box becomes the case description Operations reads first.
          Include anything the questions did not cover - the delivering account number, positions to leave behind,
          client timing requests.
        </p>
        <p style={{ margin: 0 }}>
          Below it is the accounts table - <strong>Please select the Automated Customer Account Transfer (ACAT)
          Accounts</strong> or <strong>Please select the Transfer of Assets (TOA) Accounts</strong>. Tick every
          Prosperity account involved; you can pick several. It lists the household&rsquo;s active accounts with
          Account Number, Account Details, Total Cash and Total Value. On ACAT the table appears once Additional
          Instructions has something in it.
        </p>
      </>
    ),
  },
  {
    title: 'Attach the documents, acknowledge and submit',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The attach screen says: <em>Please add any related Documents to the tiles below. Please note, missing or
          incomplete documents may result in a NIGO.</em> Add the transfer form, the contra firm statement and anything
          else you have. For a partial ACAT, add the Excel file of positions here.
        </p>
        <p style={{ margin: 0 }}>
          Read the Operations and Trading notice below the upload. Select <em>I have read, understand, and agree to
          the above information</em> under Acknowledgement and click Next. FSC creates the case and opens it. Note the
          case number for follow-up.
        </p>
      </>
    ),
  },
];

const QUESTIONS = [
  // Automated Customer Account Transfer (ACAT) Screen
  {
    screen: 'Account Transfer (ACAT) - Automated Customer Account Transfer (ACAT) Details',
    q: 'Where are the funds being directed to? (Which Custodian)',
    when: 'Always - this is the first question',
    options: ['Schwab', 'Fidelity'],
  },
  {
    q: 'What is the estimated Value?',
    when: 'Once a custodian is selected',
    note: 'A dollar amount. The remaining questions only appear once it is more than $1. It is also saved to the case Amount field.',
  },
  {
    q: 'Will this involved Qualifed Accounts, Non-Qualified Accounts or Both?',
    when: 'Once the estimated value is more than $1',
    options: ['Qualified', 'Non-Qualified', 'Both (Qualified and Non-Qualified)'],
  },
  {
    q: 'Does the delivering Registration Name match character for character?',
    when: 'After the qualified question',
    options: ['Yes (The Account Names Match Exactly)', 'No (They do not - which could result in a NIGO from the Custodian)'],
    note: 'Compare the registration name on the contra firm statement with the receiving account. The form lets you continue on No, but the option itself warns the custodian may NIGO the transfer.',
    warn: true,
  },
  {
    q: 'Bank/Institution Name (Transferring From or To)',
    when: 'After the registration name question',
    note: 'Saved to the case Bank Name field.',
  },
  {
    q: 'What type of institution is this? Fund company direct, insurance carrier, bank, trust company, recordkeeper/TPA, plan sponsor, alt sponsor. Each has a different process.',
    when: 'Once the institution name is entered',
  },
  {
    q: 'Are all positions ACAT/DTC Eligible?',
    when: 'Once the institution type is entered',
    options: ['Yes (All positions are ACAT/DTC Eligible)', 'No (Not all positions are ACAT/DTC Eligible)'],
    note: 'Help text: "Are all positions ACAT-eligible and DTC-eligible? Proprietary or house-branded funds, some alternatives, and certain non-DTC securities cannot transfer."',
  },
  {
    q: 'Is this a Full or Partial ACAT Transfer?',
    when: 'After the eligibility question',
    options: ['Full (ACAT Transfer)', 'Partial (ACAT Transfer)'],
    note: 'Choosing Partial shows: "For Partial ACAT Transfers, please add an excel file on the next screen that contains the exact positions and position quantity - we need this to properly track and ensure everything has been delivered."',
    warn: true,
  },
  {
    q: 'Will residuals credit? (How are trailing dividends/interest handled?)',
    when: 'After the full or partial question',
    options: ['Automatically (Residuals Sweep Automatically)', 'Unknown (We will follow up and let the Prosperity Operations team know)'],
  },
  {
    q: 'Do you have a statement from the Contra firm?',
    when: 'After the residuals question',
    options: ['Yes (I have a statement from the Contra firm from within the last 90 days)', 'No (I do not have a statement from the Contra firm from within the last 90 days)'],
  },
  {
    q: 'Additional Instructions',
    when: 'After the contra firm statement question',
    note: 'Becomes the case description. The ACAT accounts table appears once this has something in it.',
  },

  // Transfer of Assets (TOA) Screen
  {
    screen: 'Transfer of Assets (TOA) - Transfer of Assets (TOA) Details',
    q: 'Is this a Transfer IN or a Transfer OUT?',
    when: 'Always',
    options: ['Transfer IN (To Prosperity)', 'Transfer OUT (Out of Prosperity)'],
  },
  {
    q: 'Which Custodian',
    when: 'Always',
    options: ['Schwab', 'Fidelity'],
  },
  {
    q: 'Who is the Custodian?',
    when: 'Always',
    options: ['Schwab', 'Fidelity', 'Other'],
  },
  {
    q: 'Other Details',
    when: 'Always',
    note: 'If you chose Other above, name the custodian here.',
  },
  {
    q: 'Will this involve Qualifed Accounts, Non-Qualified Accounts or Both?',
    when: 'Always',
    options: ['Qualified', 'Non-Qualified', 'Both (Qualified and Non-Qualified)'],
  },
  {
    q: 'Does this TOA involve any of the following Entity/Fiduciary account types?',
    when: 'Always',
    options: [
      'Trust Account (exact trust name and date, current acting trustee(s), certification of trust)',
      'Corporate/LLC Account (corporate resolution and authorized signer list)',
      'Estate Account (letters testamentary, EIN, date-of-death valuation)',
      "UTMA/UGMA (state of registration, custodian, and beneficiary's date of birth)",
    ],
    note: 'You can tick more than one. Each option names the documents that account type needs - attach them on the next screen.',
  },
  {
    q: 'What type of institution is this? Fund company direct, insurance carrier, bank, trust company, recordkeeper/TPA, plan sponsor, alt sponsor. Each has a different process.',
    when: 'Always',
  },
  {
    q: 'Bank/Institution Name (Transferring From or To)',
    when: 'Always',
    note: 'Saved to the case Bank Name field.',
  },
  {
    q: 'Is this a Full or Partial Transfer of Assets?',
    when: 'Always',
    options: ['Full (Transfer of Assets)', 'Partial (Transfer of Assets)'],
    note: 'For a partial, say what is moving in Additional Instructions.',
  },
  {
    q: 'What is the estimated Value?',
    when: 'Always',
    note: 'A dollar amount. It is also saved to the case Amount field.',
  },
  {
    q: "Does the delivering institution require their own form, or will they accept the receiving custodian's TOA form?",
    when: 'Always',
    options: ['Yes (The Delivering Institution require their own form and we have it)', 'No (The delivering institution will accept our forms)'],
    note: 'If the delivering institution needs its own form, have it completed and attach it on the next screen.',
  },
  {
    q: 'Will this require a form to be Notarized or receive a Medallion Signature Guarantee?',
    when: 'Always',
    options: ['Yes (Requires Notary)', 'Yes (Requires Medallion)', 'Yes (Requires Notary and Medallion)', 'Yes (But this has been handled)', 'None of the Above'],
  },
  {
    q: 'Additional Instructions',
    when: 'Always',
    note: 'Becomes the case description.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Account Transfer of Assets'),
  {
    h: "The ACAT questions stopped after the estimated value",
    p: ['The rest of the ACAT questions only appear once the estimated value is more than $1. Enter the real estimate.'],
  },
  {
    h: "The account I expected isn't in the list",
    p: [
      'The ACAT and TOA tables list the household’s active accounts (not sleeves). An account that is inactive, or not yet set up under this household, will not appear. If the receiving account is new and not open yet, wait until it is before submitting the transfer.',
    ],
  },
  {
    h: "The ACAT accounts table hasn't appeared",
    p: ['It stays hidden until the Additional Instructions box has something in it.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function AccountTransfers() {
  return (
    <GuidePage
      title="Account Transfer Requests"
      icon="box-arrow-in-right"
      path="Operations → Account Transfer of Assets → Account Transfer (ACAT) or Transfer of Assets (TOA)"
      minutes={7}
      intro="How to submit an ACAT or a transfer of assets to the Prosperity Operations team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. Both forms ask about the delivering firm in detail, and you attach documents before you submit.</Para>
        <Bullets items={[
          <><strong>A recent statement from the delivering (contra) firm</strong> - the ACAT form asks whether you have one from within the last 90 days. Use it to check that the registration name matches character for character.</>,
          <><strong>The delivering institution&rsquo;s name and type</strong> - fund company direct, insurance carrier, bank, trust company, recordkeeper/TPA, plan sponsor or alt sponsor.</>,
          <><strong>The custodian</strong> (Schwab or Fidelity), whether the accounts are qualified, non-qualified or both, and the estimated value.</>,
          <><strong>The Prosperity accounts involved</strong>, open and under the same household. You pick them from a list.</>,
          <><strong>For a partial ACAT</strong>, an Excel file listing the exact positions and quantities to transfer.</>,
          <><strong>For a TOA</strong>, whether it is coming in or going out, whether the delivering institution needs its own form, any notary or Medallion requirement, and for trust, corporate/LLC, estate or UTMA/UGMA accounts the documents named on the form.</>,
        ]} />
        <Callout>
          ACAT positions must be ACAT- and DTC-eligible. The form&rsquo;s help text notes that proprietary or
          house-branded funds, some alternatives and certain non-DTC securities cannot transfer.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe the custodian&rsquo;s transfer process or
        timelines, or the tax treatment of the assets being moved.
      </Footnote>
    </GuidePage>
  );
}
